import React, {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useRef,
  useContext,
} from 'react';
import { type Slide } from '../_data';
import { filterOutPortfolioStorySlides } from '../portfolioStorySlideFilter';

interface SlidesContentContextType {
  slides: Slide<any>[];
  currentSlide: Slide<any>;
  type: 'premium_report' | 'presentation_of_options';
  setCurrentSlide: (slide: Slide<any>) => void;
  updateSlideContent: (
    slide: Slide<any>,
    content: Slide<any>['content'],
  ) => void;
  setCurrentSlideByIndex: (index: number) => void;
  handleSave: () => void;
}

export const SlidesContentContext = createContext<
  SlidesContentContextType | undefined
>(undefined);

export const SlidesContentProvider = ({
  children,
  action,
  handleSave,
  setDirty,
}: {
  children: ReactNode;
  action: any;
  handleSave: (slides: any) => void;
  setDirty: (dirty: boolean) => void;
}): React.ReactElement => {
  /**
   * Hide portfolio story slides for every report type: template ids 20 / 27 and any slide whose
   * title contains "Portfolio Story for" (API may not use the same ids as _data.ts).
   */
  const filteredSlides = filterOutPortfolioStorySlides(
    action.value.slides as Slide<any>[],
  );
  const [slides, setSlides] = useState<Slide<any>[]>(filteredSlides);
  const [currentSlide, setCurrentSlide] = useState<Slide<any>>(
    filteredSlides[0],
  );
  const isUpdatingRef = useRef(false);
  const [type] = useState<'premium_report' | 'presentation_of_options'>(
    action.value.type,
  );

  const setCurrentSlideByIndex = useCallback(
    (index: number) => {
      setCurrentSlide(slides[index]);
    },
    [slides],
  );

  const updateSlideContent = useCallback(
    (slide: Slide<any>, content: Slide<any>['content']) => {
      if (isUpdatingRef.current) return;
      isUpdatingRef.current = true;

      setSlides(prevSlides => {
        const slideIndex = prevSlides.findIndex(
          s => s.slideTemplateId === slide.slideTemplateId,
        );
        if (slideIndex === -1) return prevSlides;

        const newSlides = [...prevSlides];
        const updatedSlide = {
          ...prevSlides[slideIndex],
          content,
        };
        newSlides[slideIndex] = updatedSlide;

        if (slide.slideTemplateId === currentSlide.slideTemplateId) {
          setCurrentSlide(updatedSlide);
        }

        return newSlides;
      });

      setDirty(true);

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    },
    [currentSlide.slideTemplateId, setDirty],
  );

  return (
    <SlidesContentContext.Provider
      value={{
        slides,
        currentSlide,
        type,
        setCurrentSlide,
        setCurrentSlideByIndex,
        updateSlideContent,
        handleSave: () => handleSave(slides),
      }}
    >
      {children}
    </SlidesContentContext.Provider>
  );
};

export const useSlidesContent = (): SlidesContentContextType => {
  const context = useContext(SlidesContentContext);
  if (!context) {
    throw new Error(
      'useSlidesContent must be used within a SlidesContentProvider',
    );
  }
  return context;
};
